/**
 * frontend/src/components/ClubPromoBanner.jsx
 * Club membership nudge on the home page.
 *
 * [Sprint UI-6] Pops up DELAY_MS after the page mounts, once per browser
 * session (dismissing it, or opening the join sheet, keeps it away until
 * the tab is closed). Tapping the card opens ClubJoinModal — the same
 * 2₾/month sheet the voucher page uses. The admin "კლუბის ხილვადობა"
 * toggle (site setting show_club_section) turns it off entirely.
 */
import React, { useEffect, useState } from 'react';
import { Crown, X, ChevronRight } from 'lucide-react';
import ClubJoinModal from './ClubJoinModal';
import { useSiteSettings } from '../context/SiteSettingsContext';

const DELAY_MS = 3000;
const SEEN_KEY = 'pm_club_banner_seen';

export default function ClubPromoBanner() {
  const { settings } = useSiteSettings();
  const enabled = settings?.show_club_section !== false;
  const [open, setOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    try { if (sessionStorage.getItem(SEEN_KEY)) return undefined; } catch { /* private mode */ }
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, [enabled]);

  const hide = () => {
    setOpen(false);
    try { sessionStorage.setItem(SEEN_KEY, '1'); } catch { /* best-effort */ }
  };

  if (!enabled) return null;

  return (
    <>
      {open && (
        <div
          className="fixed left-4 right-4 bottom-[84px] z-40 md:left-auto md:right-6 md:bottom-6 md:w-[380px]"
          role="dialog"
          aria-label="PriceMap კლუბი"
          data-testid="club-banner"
        >
          <div
            role="button"
            tabIndex={0}
            onClick={() => { hide(); setJoinOpen(true); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); hide(); setJoinOpen(true); } }}
            className="relative bg-orange-500 text-white rounded-2xl shadow-[0_10px_30px_rgba(249,115,22,0.35)] pl-4 pr-11 py-3.5 cursor-pointer active:scale-[0.99] transition-transform"
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); hide(); }}
              aria-label="დახურვა"
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
              data-testid="club-banner-close"
            >
              <X className="w-4 h-4" strokeWidth={2.2} />
            </button>
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                <Crown className="w-5 h-5" strokeWidth={2} />
              </span>
              <div className="min-w-0">
                <p className="m-0 text-[13.5px] leading-snug font-medium">
                  გახდი PriceMap კლუბის წევრი თვეში 2₾-ად და მიიღე ულიმიტო გამოყენების ვაუჩერები ყველა ფასდაკლებაზე.
                </p>
                <div className="mt-1.5 text-[13px] font-bold flex items-center gap-0.5">
                  გაწევრიანება
                  <ChevronRight className="w-4 h-4" strokeWidth={2.4} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ClubJoinModal open={joinOpen} onClose={() => setJoinOpen(false)} />
    </>
  );
}
